import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { Flex } from '@chakra-ui/react';
import userflow from 'userflow.js';

import { FeatureFlags } from '../../../components/Authorizer';
import useCompanyFeatures from '../../../components/Authorizer/useCompanyFeatures';
import CertificateCreationFlow from '../../../components/CertificateCreationFlow';
import LoadingSpinner from '../../../components/Spinner';
import CompanySettingTypes from '../../../constants/companySettings';
import { FeatureTypes } from '../../../constants/featureTypes';
import { userflowConstants } from '../../../constants/userflowConstants';
import {
  getInsuranceRequest,
  getRequirements,
  selectAgentPortalLoading,
  selectAgentPortalUser,
  selectAgentPortalVerified,
  selectCompany,
  selectInsuranceRequirements,
  selectInsured, selectInsuredCompanyFeatures,
  selectInsuredId,
  selectJob,
  selectJobId,
  selectPolicyTypes, selectRecordId,
  selectVerifyFailureCount,
  updateAgentPortalIsLoading
} from '../../../redux/modules/agentPortal';
import { getInsuranceRequirement, selectCurrentRequirement } from '../../../redux/modules/insured';
import { agentCertificateCreator } from '../../../redux/reducer';
import { getContentHeightString } from '../../../styles/layoutDimensions';
import UnverifiedBlock from '../components/UnverifiedBlock';

import NoRecordExistsBlock from './NoRecordExistsBlock';

const SubmissionPage = () => {
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const { insuranceRequestId } = useParams();
  const apiToken = window.localStorage.getItem('portal-api-token');
  const apiTokenDate = window.localStorage.getItem('portal-api-token-date');
  const verified = useSelector(selectAgentPortalVerified);
  const user = useSelector(selectAgentPortalUser);
  const insuredId = useSelector(selectInsuredId);
  const jobId = useSelector(selectJobId);
  const recordId = useSelector(selectRecordId);
  const insuranceRequirements = useSelector(selectInsuranceRequirements);
  const insuranceRequirement = useSelector(selectCurrentRequirement);
  const company = useSelector(selectCompany);
  const job = useSelector(selectJob);
  const insured = useSelector(selectInsured);
  const policyTypes = useSelector(selectPolicyTypes);
  const isLoading = useSelector(selectAgentPortalLoading);
  const verifyFailureCount = useSelector(selectVerifyFailureCount);
  const companyInsuredFeatures = useSelector(selectInsuredCompanyFeatures);
  const { companyHasFeature: ocrEnabled } = useCompanyFeatures(FeatureFlags.OCR, companyInsuredFeatures);

  const aiScanEnabled = useMemo(() => companyInsuredFeatures.find(f => [
    FeatureTypes.AiTierOne,
    FeatureTypes.AiTierTwo,
    FeatureTypes.AiTierThree
  ].includes(f.name))?.isEnabled, [companyInsuredFeatures]);

  const requireAgentDetails = useMemo(() => !!company?.settings?.find(s => s.name === CompanySettingTypes.RequireAgentDetails)?.isEnabled, [company]);

  useEffect(() => {
    if (apiToken && apiTokenDate > Date.now()) {
      dispatch(getInsuranceRequest(insuranceRequestId));
    } else {
      dispatch(updateAgentPortalIsLoading(false));
    }
  }, [insuranceRequestId]);

  useEffect(() => {
    if (user) {
      userflow.init(userflowConstants.token);
      userflow.identify(user.userId, {
        user_id: user.userId,
        name: `${user.userFirstName} ${user.userLastName}`,
        email: user.userEmail,
        userLevel: user.userLevel,
        userRoles: ['agent'],
        pinsVersion: { set_once: '2.0' },
        last_signed_in_at: new Date().toISOString() // ISO 8601 format
      });

      userflow.group('agents');

      window.heap.identify(user.userEmail);
      window.heap.addUserProperties({
        email: user.userEmail,
        isSubmitter: true,
        userId: user.userId,
        pinsVersion: '2.0'
      });
    }
  }, [apiToken, dispatch, insuranceRequestId, user]);

  useEffect(() => {
    if (apiToken && apiTokenDate && insuredId && jobId && recordId) {
      dispatch(getRequirements(insuranceRequestId, insuredId, jobId, recordId));
      dispatch(getInsuranceRequirement(recordId));
    }
  }, [apiToken, dispatch, insuredId, jobId, recordId]);

  if (isLoading) {
    return (
      <Flex direction="column" justifyContent="center" maxH="50vh">
        <LoadingSpinner isInline minHeight="400px" />
      </Flex>
    );
  }

  if ((!apiToken || !verified || !apiTokenDate) && !isLoading) {
    return <UnverifiedBlock currentUrlSlug="submission" verifyFailureCount={verifyFailureCount} />;
  }

  if (!insuranceRequirement || !Object.keys(insuranceRequirement)?.length || !!insuranceRequirement?.archivedAt) {
    return <NoRecordExistsBlock />;
  }

  return (
    <Flex alignItems="stretch" minHeight={getContentHeightString()}>
      {insuranceRequirements && (
      <CertificateCreationFlow
        blueprint={agentCertificateCreator}
        company={company}
        documentRequirements={insuranceRequirements.documentRequirements}
        insuranceRequirement={insuranceRequirement}
        insured={insured}
        insuredRequirements={insuranceRequirements.policyRequirements}
        itemRequirements={insuranceRequirements.itemRequirements}
        job={job}
        languageRequirements={insuranceRequirements.languageRequirements}
        options={{
          ocr: true,
          aiScan: aiScanEnabled,
          showCertHolderInformation: true,
          showInsuredInformation: true,
          showPreviousSubmissions: true,
          updateInsuredProfilePage: false,
          sendSubmissionEmail: true,
          afterSubmitRedirectUrl: url.replace('submission', 'confirmation'),
          shouldSync: false,
          requireAgentDetails
        }}
        policyReviewContentHeight="68vh"
        policyTypes={policyTypes}
        user={user}
      />
      )}
    </Flex>
  );
};

SubmissionPage.propTypes = {};

export default SubmissionPage;
